
import { Product as Cultivar, TerpeneWithPercentage } from "@/types";
import Papa from 'papaparse';
import { supabase } from "@/integrations/supabase/client";
import { detectBrowserRestrictions } from "@/lib/utils";

// Helper function to get all database columns for a specific table
export const getTableColumns = async (tableName: string): Promise<Array<{column_name: string, data_type: string}>> => {
  try {
    console.log(`Fetching schema information for table: ${tableName}`);
    const { data, error } = await supabase.rpc('get_table_columns', { table_name: tableName });
    
    if (error) {
      console.error(`Error fetching columns for ${tableName}:`, error);
      throw error;
    }
    
    if (!data || !data.length) {
      console.warn(`No columns found for table ${tableName}`);
      return [];
    } else {
      console.log(`Retrieved ${data.length} columns for ${tableName}:`, data);
    }
    
    return data || [];
  } catch (error) {
    console.error(`Error fetching columns for ${tableName}:`, error);
    throw new Error(`Failed to get table schema for ${tableName}: ${error instanceof Error ? error.message : String(error)}`);
  }
};

// Helper to convert snake_case to Title Case for display purposes
const formatColumnName = (column: string) => {
  return column
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Enhanced helper function to download CSV with better error handling, browser detection, and fallback mechanisms
const downloadCSV = async (csvContent: string, filename: string): Promise<{ success: boolean, data?: string, error?: string, count?: number }> => {
  try {
    console.log(`Preparing to download CSV: ${filename}`);
    
    // Always return the CSV content for potential fallback display
    const result = { 
      success: false, 
      data: csvContent,
      count: csvContent.split('\n').length - 1 // Estimate row count (header row subtracted)
    };
    
    // Check for browser restrictions
    const restrictions = detectBrowserRestrictions();
    console.log(`Browser environment check:`, restrictions);
    
    if (restrictions.downloadBlocked) {
      console.log(`Download blocked by browser restrictions, using data return fallback`);
      return { 
        ...result,
        error: "Browser restrictions prevented automatic download. Use the provided data instead." 
      };
    }

    // Standard download method for unrestricted environments
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    console.log(`Created blob URL: ${url}`);
    
    // Create a hidden link and trigger download
    return new Promise((resolve) => {
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      console.log(`Appending link to document body`);
      document.body.appendChild(link);
      
      // Give the browser a moment to process
      setTimeout(() => {
        console.log(`Clicking download link`);
        link.click();
        
        // Clean up with a delay to ensure download starts
        setTimeout(() => {
          console.log(`Cleaning up download resources`);
          document.body.removeChild(link);
          URL.revokeObjectURL(url); // Properly release the object URL
          
          // Return success but also include CSV data as fallback
          resolve({
            success: true,
            data: csvContent,
            count: result.count
          });
        }, 200);
      }, 100);
    });
  } catch (error) {
    console.error('Error downloading CSV:', error);
    return { 
      success: false, 
      data: csvContent,
      error: 'Failed to download file: ' + (error instanceof Error ? error.message : String(error)),
      count: csvContent.split('\n').length - 1
    };
  }
};

// Process value based on its data type for CSV export
const processValueForExport = (value: any, dataType: string): string => {
  if (value === undefined || value === null) {
    return '';
  }
  
  if (dataType.includes('ARRAY')) {
    return Array.isArray(value) ? value.join(';') : '';
  }
  
  if (dataType === 'json' || dataType === 'jsonb') {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
  
  return String(value);
};

// Process value based on its data type from CSV import
const processValueForImport = (value: any, dataType: string): any => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  
  if (dataType.includes('ARRAY')) {
    return value.split(';').filter(Boolean);
  }
  
  if (dataType === 'json' || dataType === 'jsonb') {
    try {
      return typeof value === 'string' ? JSON.parse(value) : value;
    } catch {
      return {};
    }
  }
  
  if (dataType === 'numeric' || dataType.includes('int') || dataType === 'decimal') {
    const num = Number(value);
    return isNaN(num) ? null : num;
  }
  
  if (dataType === 'boolean') {
    return value === 'true' || value === true;
  }
  
  return value;
};

// Dynamic export to CSV for any table - now with enhanced error handling and fallback mechanisms
export const exportDynamicToCSV = async (tableName: string, data: any[]) => {
  try {
    console.log(`Starting export to CSV for ${tableName} with ${data.length} rows`);
    
    // Get column metadata from the database
    const columns = await getTableColumns(tableName);
    
    if (!columns.length) {
      console.error(`No columns retrieved for ${tableName}, cannot proceed with export`);
      throw new Error(`Could not retrieve column information for ${tableName}`);
    }
    
    console.log(`Retrieved ${columns.length} columns for ${tableName}`);
    
    // Process the data to handle special types
    const processedData = data.map(item => {
      const result: Record<string, any> = {};
      
      columns.forEach(col => {
        result[col.column_name] = processValueForExport(item[col.column_name], col.data_type);
      });
      
      return result;
    });
    
    // Generate CSV
    console.log(`Generating CSV content for ${tableName}`);
    const csv = Papa.unparse(processedData);
    console.log(`CSV generation successful, content length: ${csv.length} characters`);
    
    // Attempt download with enhanced method that returns results
    const downloadResult = await downloadCSV(csv, `${tableName}-export-${new Date().toISOString().split('T')[0]}.csv`);
    console.log(`Download result:`, downloadResult);
    
    // Always return both success status and the CSV data for fallback display in UI
    return {
      success: downloadResult.success,
      data: csv,
      error: downloadResult.error,
      count: processedData.length
    };
  } catch (error) {
    console.error(`Error exporting ${tableName} to CSV:`, error);
    throw error;
  }
};

// Generate a template CSV file based on the table structure
export const exportDynamicTemplate = async (tableName: string) => {
  try {
    console.log(`Generating template for ${tableName}`);
    
    // Get column metadata from the database
    const columns = await getTableColumns(tableName);
    
    if (!columns.length) {
      console.error(`No columns retrieved for ${tableName}, cannot generate template`);
      throw new Error(`Could not retrieve column information for ${tableName}`);
    }
    
    console.log(`Retrieved ${columns.length} columns for ${tableName} template`);
    
    // Create an example row with appropriate values for each column type
    const exampleRow: Record<string, any> = {};
    const columnsToInclude = columns.filter(col => 
      !['id', 'created_at', 'updated_at'].includes(col.column_name)
    );
    
    columnsToInclude.forEach(col => {
      switch (col.data_type) {
        case 'text':
          exampleRow[col.column_name] = `Example ${formatColumnName(col.column_name)}`;
          break;
        case 'numeric':
          exampleRow[col.column_name] = '0.00';
          break;
        case 'integer':
        case 'bigint':
          exampleRow[col.column_name] = '0';
          break;
        case 'boolean':
          exampleRow[col.column_name] = 'false';
          break;
        case 'date':
          exampleRow[col.column_name] = new Date().toISOString().split('T')[0];
          break;
        case 'timestamp with time zone':
          exampleRow[col.column_name] = new Date().toISOString();
          break;
        default:
          if (col.data_type.includes('ARRAY')) {
            exampleRow[col.column_name] = 'value1;value2;value3';
          } else if (col.data_type === 'json' || col.data_type === 'jsonb') {
            exampleRow[col.column_name] = '{}';
          } else {
            exampleRow[col.column_name] = '';
          }
      }
    });
    
    // Generate CSV with column headers
    console.log(`Generating template CSV content`);
    const csv = Papa.unparse([exampleRow]);
    console.log(`Template CSV generation successful, content length: ${csv.length} characters`);
    
    // Attempt download with enhanced method that returns results
    const downloadResult = await downloadCSV(csv, `${tableName}-template-${new Date().toISOString().split('T')[0]}.csv`);
    console.log(`Template download result:`, downloadResult);
    
    // Always return both success status and the CSV data for fallback display in UI
    return {
      success: downloadResult.success,
      data: csv,
      error: downloadResult.error
    };
  } catch (error) {
    console.error(`Error generating template for ${tableName}:`, error);
    throw error;
  }
};

// Dynamic import from CSV for any table
export const importDynamicFromCSV = async (tableName: string, file: File) => {
  try {
    console.log(`Importing CSV to ${tableName}`);
    
    // Get column metadata from the database
    const columns = await getTableColumns(tableName);
    
    if (!columns.length) {
      console.error(`No columns retrieved for ${tableName}, cannot proceed with import`);
      throw new Error(`Could not retrieve column information for ${tableName}`);
    }
    
    console.log(`Retrieved ${columns.length} columns for ${tableName} import`);
    
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            console.log(`Parsed ${results.data.length} rows from CSV`);
            
            if (results.errors && results.errors.length > 0) {
              console.warn(`CSV parse warnings:`, results.errors);
            }
            
            const processedData = results.data.map((row: any) => {
              const item: Record<string, any> = {};
              
              columns.forEach(col => {
                // Only process columns that exist in the CSV
                if (row[col.column_name] !== undefined) {
                  item[col.column_name] = processValueForImport(row[col.column_name], col.data_type);
                }
              });
              
              return item;
            });
            
            console.log(`Processed ${processedData.length} rows for import`);
            resolve(processedData);
          } catch (error) {
            console.error('Error processing CSV data:', error);
            reject(error);
          }
        },
        error: (error) => {
          console.error('Error parsing CSV file:', error);
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error(`Error importing CSV to ${tableName}:`, error);
    throw error;
  }
};

// Backwards compatibility functions
export const exportProductsToCSV = (products: Cultivar[]) => {
  return exportDynamicToCSV('cultivars', products);
};

export const exportCultivarsTemplate = async () => {
  return exportDynamicTemplate('cultivars');
};

export const exportCultivarsToCSV = (cultivars: Cultivar[]) => {
  return exportDynamicToCSV('cultivars', cultivars);
};

export const checkForDuplicateCultivars = async (cultivars: string[]): Promise<string[]> => {
  if (!cultivars.length) return [];
  
  const { data, error } = await supabase
    .from('cultivars')
    .select('cultivar')
    .in('cultivar', cultivars);
    
  if (error) {
    console.error("Error checking for duplicates:", error);
    throw new Error('Failed to check for duplicates');
  }
  
  return data.map(item => item.cultivar);
};

export const importProductsFromCSV = async (file: File): Promise<Cultivar[]> => {
  const data = await importDynamicFromCSV('cultivars', file) as any[];
  
  // Extra processing for cultivar specific fields if needed
  return data.map(item => {
    // Map to Cultivar type with appropriate defaults
    return {
      id: item.id || '',
      cultivar: item.cultivar || 'Unnamed Cultivar',
      description: item.description || '',
      format: item.format || 'flower',
      // ... handle special fields as needed, but most will be handled by the dynamic import
      images: Array.isArray(item.images) ? item.images : [],
      terpene_info: {
        major_terpenes: Array.isArray(item.primary_terpenes) ? item.primary_terpenes : [],
        total_terpenes_percentage: Number(item.total_terpenes_percentage || 0)
      },
      created_at: item.created_at || new Date().toISOString(),
      updated_at: item.updated_at || new Date().toISOString(),
      // Include all other fields dynamically
      ...item
    } as Cultivar;
  });
};
